import {
  LEDModes,
  MachinePublicHydrated,
  StrikeZone as StrikeZoneConfig,
  UnifiedConfig,
} from "@aura-frontend/omnibus-az-client";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertBar,
  Button,
  cn,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
  Icons,
  useToast,
} from "@win-reality/react-ui";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { type DefaultValues, useForm } from "react-hook-form";
import { useBlocker } from "react-router-dom";

import { StrikeZone } from "@/components/strikeZone/StrikeZone";
import {
  Field,
  type FieldDimensions,
  type FieldValues,
} from "@/components/ui/FieldConfiguration/Field";
import {
  DIMENSIONS_BY_SPORT,
  MOUND_DISTANCE_BY_SPORT,
} from "@/components/ui/FieldConfiguration/fieldDefaults";
import { PageSpinner } from "@/components/ui/PageSpinner/PageSpinner";
import { useNavMenu } from "@/components/ui/SideMenu";
import { committedModeFor } from "@/utils/machineLedMode";

import { useUpdateUnifiedConfig } from "./FieldConfiguration.hooks";
import {
  type FieldConfigurationFormValues,
  fieldConfigurationSchema,
} from "./FieldConfiguration.schema";
import { LEDBoardConfiguration } from "./LEDBoardConfiguration";
import { useLedEntitlementsDetails } from "./useLedEntitlementsDetails";
import { useUpdateMachineLedMode } from "./useUpdateMachineLedMode";

type FieldConfigurationProps = {
  field?: MachinePublicHydrated;
  unifiedConfig?: UnifiedConfig;
  strikeZoneConfig?: StrikeZoneConfig;
  isLoadingField?: boolean;
  isLoadingUnifiedConfig?: boolean;
  isLoadingStrikeZoneConfig?: boolean;
};

const SET_OPTS = { shouldDirty: true, shouldValidate: true } as const;

function withSportDefaults(
  values: DefaultValues<FieldConfigurationFormValues>,
): DefaultValues<FieldConfigurationFormValues> {
  const sport = values.sport;
  if (sport !== "baseball" && sport !== "softball") return values;
  const dims = DIMENSIONS_BY_SPORT[sport];
  const mound = MOUND_DISTANCE_BY_SPORT[sport];
  return {
    ...values,
    moundDistance: values.moundDistance || mound,
    baseDistance: values.baseDistance || dims.diamondSizeFeet,
    infieldAtFoulLine: values.infieldAtFoulLine || dims.infieldAtFoulLineFeet,
    fenceFirstBase: values.fenceFirstBase || dims.fenceFirstBaseFeet,
    fenceThirdBase: values.fenceThirdBase || dims.fenceThirdBaseFeet,
    leftField: values.leftField || dims.fenceLeftFieldFeet,
    centerField: values.centerField || dims.fenceCenterFieldFeet,
    rightField: values.rightField || dims.fenceRightFieldFeet,
  };
}

function computeDefaults(
  unifiedConfig: UnifiedConfig,
  strikeZoneConfig: StrikeZoneConfig | null | undefined,
  committedLedMode: LEDModes | null,
  committedEndTimeISO: string | null,
): DefaultValues<FieldConfigurationFormValues> {
  const f = unifiedConfig.field;
  return withSportDefaults({
    sport: unifiedConfig.sport as "baseball" | "softball" | undefined,
    level: f?.ageGroup,
    moundDistance: unifiedConfig.rubberDistanceF ?? 0,
    baseDistance: f?.diamondSizeFeet ?? 0,
    infieldAtFoulLine: f?.infieldAtFoulLineFeet ?? 0,
    fenceFirstBase: f?.fenceFirstBaseFeet ?? 0,
    fenceThirdBase: f?.fenceThirdBaseFeet ?? 0,
    leftField: f?.fenceLeftFieldFeet ?? 0,
    centerField: f?.fenceCenterFieldFeet ?? 0,
    rightField: f?.fenceRightFieldFeet ?? 0,
    strikeZone: strikeZoneConfig ?? undefined,
    ledBoardMode: committedLedMode,
    ledEndTimeISO: committedEndTimeISO,
    ledOrgId: null,
  });
}

function FieldConfiguration({
  field,
  unifiedConfig,
  strikeZoneConfig,
  isLoadingField,
  isLoadingUnifiedConfig,
  isLoadingStrikeZoneConfig,
}: FieldConfigurationProps) {
  if (
    isLoadingField ||
    isLoadingUnifiedConfig ||
    isLoadingStrikeZoneConfig ||
    !field ||
    !unifiedConfig
  ) {
    return <PageSpinner />;
  }

  return (
    <FieldConfigurationForm
      field={field}
      unifiedConfig={unifiedConfig}
      strikeZoneConfig={strikeZoneConfig}
    />
  );
}

type FieldConfigurationFormProps = {
  field: MachinePublicHydrated;
  unifiedConfig: UnifiedConfig;
  strikeZoneConfig?: StrikeZoneConfig;
};

function FieldConfigurationForm({
  field,
  unifiedConfig,
  strikeZoneConfig,
}: FieldConfigurationFormProps) {
  const { isCollapsed } = useNavMenu();
  const { pending } = useToast();
  const { mutateAsync: updateUnifiedAsync } = useUpdateUnifiedConfig(
    field.machineUuid ?? "",
  );
  const { mutateAsync: updateLedAsync } = useUpdateMachineLedMode();
  const skipBlockerRef = useRef(false);

  const committedLedMode = useMemo(
    () => committedModeFor(unifiedConfig.machine ?? field),
    [unifiedConfig.machine, field],
  );

  const ledEntitlements = useLedEntitlementsDetails(
    unifiedConfig.machine ?? field,
  );
  const committedEndTimeISO = useMemo(() => {
    const ts = ledEntitlements?.lastActiveOccurrence?.endTime;
    return ts ? new Date(ts * 1000).toISOString() : null;
  }, [ledEntitlements]);

  const defaultValues = useMemo(
    () =>
      computeDefaults(
        unifiedConfig,
        strikeZoneConfig,
        committedLedMode,
        committedEndTimeISO,
      ),
    // Compute once on mount — useForm only reads defaultValues on first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const form = useForm<FieldConfigurationFormValues>({
    resolver: zodResolver(fieldConfigurationSchema),
    mode: "onChange",
    defaultValues,
  });

  const isValid = form.formState.isValid;
  const isDirty = form.formState.isDirty;
  const isSubmitting = form.formState.isSubmitting;
  const values = form.watch();

  useEffect(() => {
    form.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // StrikeZone re-commits its initial state via onChange once after presets
  // load. When the page hydrated with an existing strike zone, re-baseline so
  // that auto-flip doesn't enable Update on mount. Skip this entirely when
  // there was no initial strike zone — the first onChange in that case is the
  // user's own choice and should mark the form dirty.
  const hadInitialStrikeZone = !!defaultValues.strikeZone;
  const hasSyncedRef = useRef(!hadInitialStrikeZone);
  useEffect(() => {
    if (hasSyncedRef.current) return;
    if (values.strikeZone) {
      hasSyncedRef.current = true;
      form.reset(form.getValues());
    }
  }, [values.strikeZone, form]);

  const blocker = useBlocker(() => isDirty && !skipBlockerRef.current);

  // Handlers must be stable — StrikeZone's onChange effect uses `onChange` in
  // its deps, so a new identity per render triggers an infinite re-render loop
  // when any other field changes.
  const handleSportChange = useCallback(
    (sport: "baseball" | "softball") => {
      form.setValue("sport", sport, SET_OPTS);
      const current = form.getValues();
      const defaults = DIMENSIONS_BY_SPORT[sport];
      const moundDefault = MOUND_DISTANCE_BY_SPORT[sport];

      const fillIfUnset = (
        key: keyof FieldConfigurationFormValues,
        defaultValue: number,
      ) => {
        const v = current[key];
        if (typeof v !== "number" || v <= 0) {
          form.setValue(key, defaultValue, SET_OPTS);
        }
      };

      fillIfUnset("moundDistance", moundDefault);
      fillIfUnset("baseDistance", defaults.diamondSizeFeet);
      fillIfUnset("infieldAtFoulLine", defaults.infieldAtFoulLineFeet);
      fillIfUnset("fenceFirstBase", defaults.fenceFirstBaseFeet);
      fillIfUnset("fenceThirdBase", defaults.fenceThirdBaseFeet);
      fillIfUnset("leftField", defaults.fenceLeftFieldFeet);
      fillIfUnset("centerField", defaults.fenceCenterFieldFeet);
      fillIfUnset("rightField", defaults.fenceRightFieldFeet);

      // Clear strike zone so the user is forced to pick a new preset for the
      // new sport. Schema marks strikeZone as required, so the form goes
      // invalid until they choose. Cast: zod's inferred type doesn't include
      // undefined, but at runtime we want "missing" → fails validation.
      form.setValue("strikeZone", undefined as never, SET_OPTS);
    },
    [form],
  );

  const handleLevelChange = useCallback(
    (level: FieldValues["level"]) => {
      if (level) form.setValue("level", level, SET_OPTS);
    },
    [form],
  );

  const handleMoundDistanceChange = useCallback(
    (moundDistance: number) =>
      form.setValue("moundDistance", moundDistance, SET_OPTS),
    [form],
  );

  const handleDimensionsSave = useCallback(
    (dims: FieldDimensions) => {
      if (dims.diamondSizeFeet !== undefined)
        form.setValue("baseDistance", dims.diamondSizeFeet, SET_OPTS);
      if (dims.infieldAtFoulLineFeet !== undefined)
        form.setValue(
          "infieldAtFoulLine",
          dims.infieldAtFoulLineFeet,
          SET_OPTS,
        );
      if (dims.fenceFirstBaseFeet !== undefined)
        form.setValue("fenceFirstBase", dims.fenceFirstBaseFeet, SET_OPTS);
      if (dims.fenceThirdBaseFeet !== undefined)
        form.setValue("fenceThirdBase", dims.fenceThirdBaseFeet, SET_OPTS);
      if (dims.fenceLeftFieldFeet !== undefined)
        form.setValue("leftField", dims.fenceLeftFieldFeet, SET_OPTS);
      if (dims.fenceCenterFieldFeet !== undefined)
        form.setValue("centerField", dims.fenceCenterFieldFeet, SET_OPTS);
      if (dims.fenceRightFieldFeet !== undefined)
        form.setValue("rightField", dims.fenceRightFieldFeet, SET_OPTS);
    },
    [form],
  );

  const handleStrikeZoneChange = useCallback(
    (sz: StrikeZoneConfig) => form.setValue("strikeZone", sz, SET_OPTS),
    [form],
  );

  const handleLedStageChange = useCallback(
    (
      mode: LEDModes | null,
      endTimeISO: string | null,
      orgId: string | null,
    ) => {
      form.setValue("ledBoardMode", mode, SET_OPTS);
      form.setValue("ledEndTimeISO", endTimeISO, SET_OPTS);
      form.setValue("ledOrgId", orgId, SET_OPTS);
    },
    [form],
  );

  const fieldValuesView: FieldValues = {
    sport: values.sport,
    level: values.level,
    rubberDistanceF: values.moundDistance,
    diamondSizeFeet: values.baseDistance ?? 0,
    infieldAtFoulLineFeet: values.infieldAtFoulLine ?? 0,
    fenceFirstBaseFeet: values.fenceFirstBase ?? 0,
    fenceThirdBaseFeet: values.fenceThirdBase ?? 0,
    fenceLeftFieldFeet: values.leftField ?? 0,
    fenceCenterFieldFeet: values.centerField ?? 0,
    fenceRightFieldFeet: values.rightField ?? 0,
  };

  const handleSubmit = async (submitValues: FieldConfigurationFormValues) => {
    if (!field.name) return;
    skipBlockerRef.current = true;
    const toastHandle = pending("Saving changes…");
    const payload = {
      sport: submitValues.sport,
      rubberDistanceF: submitValues.moundDistance,
      ...(submitValues.strikeZone && {
        strikeZone: submitValues.strikeZone,
      }),
    };
    try {
      await updateUnifiedAsync({
        ...payload,
        field: {
          ageGroup: submitValues.level,
          name: field.name,
          diamondSizeFeet: submitValues.baseDistance ?? 0,
          infieldAtFoulLineFeet: submitValues.infieldAtFoulLine ?? 0,
          fenceFirstBaseFeet: submitValues.fenceFirstBase ?? 0,
          fenceThirdBaseFeet: submitValues.fenceThirdBase ?? 0,
          fenceLeftFieldFeet: submitValues.leftField ?? 0,
          fenceCenterFieldFeet: submitValues.centerField ?? 0,
          fenceRightFieldFeet: submitValues.rightField ?? 0,
        },
      });

      const modeChanged = submitValues.ledBoardMode !== committedLedMode;
      // For time-requiring modes, also re-send when only the end time was
      // edited — the user can tweak the schedule by re-clicking the committed
      // mode and picking a different time.
      const endTimeChanged =
        submitValues.ledBoardMode !== null &&
        submitValues.ledEndTimeISO !== committedEndTimeISO;
      if (modeChanged || endTimeChanged) {
        await updateLedAsync({
          targetMode: submitValues.ledBoardMode,
          endTimeISO: submitValues.ledEndTimeISO ?? null,
          orgId: submitValues.ledOrgId ?? null,
          machine: unifiedConfig.machine ?? field,
        });
      }

      form.reset(submitValues);
      toastHandle.success("Field settings updated", { duration: 1000 });
    } catch {
      toastHandle.error("Failed to update field settings");
    } finally {
      skipBlockerRef.current = false;
    }
  };

  return (
    <>
      <div className="px-5 py-6 mx-auto flex flex-col gap-8 pb-24">
        {field.currentGame && (
          <div className="max-w-128 mx-auto w-full">
            <AlertBar
              icon={<Icons.InfoIconOutline className="size-5" />}
              variant="info"
            >
              Field settings are currently being applied by the active event.
              You can update them here to make instant changes, but will be
              overridden by the next scheduled event.
            </AlertBar>
          </div>
        )}

        <div className="max-w-128 w-full mx-auto">
          <LEDBoardConfiguration
            field={field}
            unifiedMachine={unifiedConfig.machine ?? undefined}
            pendingMode={values.ledBoardMode}
            committedMode={committedLedMode}
            committedEndTimeISO={committedEndTimeISO}
            onStageChange={handleLedStageChange}
          />
        </div>

        <div className="max-w-128 w-full mx-auto">
          <Field
            values={fieldValuesView}
            onSportChange={handleSportChange}
            onLevelChange={handleLevelChange}
            onMoundDistanceChange={handleMoundDistanceChange}
            onDimensionsSave={handleDimensionsSave}
          />
        </div>

        <div className="max-w-128 w-full mx-auto">
          <StrikeZone
            key={values.sport ?? "baseball"}
            sport={values.sport ?? "baseball"}
            values={values.strikeZone ?? null}
            onChange={handleStrikeZoneChange}
          />
        </div>
      </div>

      <div
        className={cn(
          "fixed bottom-0 left-0 flex w-full items-center justify-center bg-white py-4 shadow-scroller-top",
          !isCollapsed ? "md:pl-64" : "md:pl-18",
        )}
      >
        <div className="mx-auto flex w-130 px-5 items-center justify-end">
          <Button
            variant="secondary-flat-light"
            isDisabled={!isDirty || !isValid}
            isLoading={isSubmitting}
            onClick={form.handleSubmit(handleSubmit)}
          >
            Update
          </Button>
        </div>
      </div>

      <Dialog
        open={blocker.state === "blocked"}
        onOpenChange={(open) => !open && blocker.reset?.()}
        defaultCancelButton={false}
        size="sm"
      >
        {({ showDivider, setScrollContainer }) => (
          <>
            <DialogScrollableContent setScrollContainer={setScrollContainer}>
              <DialogHeader>
                <DialogTitle>Are you sure?</DialogTitle>
              </DialogHeader>
              <p className="text-md">
                You have unsaved field settings. If you leave now, your changes
                will be lost.
              </p>
            </DialogScrollableContent>
            <DialogFooter showDivider={showDivider}>
              <div className="flex w-full justify-between">
                <Button
                  variant="ghost"
                  className="underline"
                  onClick={() => blocker.reset?.()}
                >
                  Back
                </Button>
                <Button
                  variant="secondary-flat-light"
                  onClick={() => blocker.proceed?.()}
                >
                  Continue
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </Dialog>
    </>
  );
}

export { FieldConfiguration };
